// pages/sales/AddSalePage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from 'antd';
import {
    Trash2,
    Plus,
    ArrowLeft,
    Save,
    X,
    RotateCcw,
} from 'react-feather';
import Select from 'react-select';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { saleActions } from '../../core/action/saleActions';
import { customerActions } from '../../core/action/customerActions';
import { productActions } from '../../core/action/productActions';
import AddCustomer from '../../core/modals/peoples/AddCustomer';

const AddSalePage = () => {
    const navigate = useNavigate();
    const searchInputRef = useRef(null);
    const productSearchTimeout = useRef(null);
    const searchDropdownRef = useRef(null);

    // Generate unique invoice/bill number
    const generateBillNumber = () => {
        const date = new Date();
        const year = date.getFullYear().toString().substr(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `Bill L${year}${month}${day}-S${random}`;
    };

    const generateReferenceNo = () => {
        const date = new Date();
        const year = date.getFullYear().toString().substr(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `INV${year}${month}${day}${random}`;
    };

    // Rounding function
    const roundAmount = (amount) => {
        return Math.round(amount);
    };

    // Initial state
    const [formData, setFormData] = useState({
        bill_number: generateBillNumber(),
        sales_reference: generateReferenceNo(),
        sale_date: dayjs().format('YYYY-MM-DD'),
        customer_id: '',
        customer_name: '',
        customer_phone: '',
        no_of_items: 0,
        total_qty: 0,
        exd_tax_total: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
        subtotal: 0,
        spl_disc_percent: 0,
        discount_amount: 0,
        adjustment: 0,
        additional_charge: 0,
        courier_charge: 0,
        grand_total: 0,
        rounded_grand_total: 0,
        round_off: 0,
        enable_rounding: true,
        po_number: '',
        dc_number: '',
        remarks: '',
        status: 'pending',
        payment_mode: '',
        payment_status: 'unpaid',
        payment_history: [],
        items: []
    });

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showProductSearch, setShowProductSearch] = useState(false);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [customerError, setCustomerError] = useState('');
    // Raw string values for numeric inputs (allows typing 0.00045 etc.)
    const [rawInputValues, setRawInputValues] = useState({});

    // Cache for product searches
    const productCache = useRef(new Map());

    // Close product search dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target)) {
                setShowProductSearch(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'processing', label: 'Processing' },
        { value: 'shipped', label: 'Shipped' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Payment mode options
    const paymentModeOptions = [
        { value: 'cash', label: 'Cash' },
        { value: 'card', label: 'Card' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'upi', label: 'UPI' },
        { value: 'cheque', label: 'Cheque' }
    ];

    // Payment status options
    const paymentStatusOptions = [
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'partial', label: 'Partial' },
        { value: 'paid', label: 'Paid' }
    ];

    // Load customers
    const loadCustomers = useCallback(async (search = '') => {
        try {
            setLoadingCustomers(true);
            setCustomerError('');

            const params = { limit: 50 };
            if (search) {
                params.search = search;
            }

            const result = await customerActions.getCustomers(params);

            if (result.success) {
                if (result.data && result.data.length > 0) {
                    const customerList = result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} ${c.phone ? `(${c.phone})` : ''}`,
                        phone: c.phone,
                        email: c.email,
                        address: c.address,
                        ...c
                    }));
                    setCustomers(customerList);
                } else {
                    setCustomers([]);
                }
            } else {
                setCustomerError('Failed to load customers');
                console.error('Error loading customers:', result.message);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
            setCustomerError('Error loading customers');
        } finally {
            setLoadingCustomers(false);
        }
    }, []);

    // Load initial customers
    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    // Handle customer search
    const handleCustomerSearch = (inputValue) => {
        if (inputValue.length >= 2) {
            loadCustomers(inputValue);
        }
        return inputValue;
    };

    // Fetch last price for customer-product
    const fetchLastPriceForCustomer = async (customerId, productId) => {
        if (!customerId || !productId) return null;

        try {
            const result = await productActions.getLastPrice(customerId, productId);
            if (result.success && result.data && result.data.last_price > 0) {
                return result.data;
            }
        } catch (error) {
            console.error('Error fetching last price:', error);
        }
        return null;
    };

    // Optimized product search with caching and last price
    const searchProducts = async (term) => {
        if (term.length < 2) {
            setSearchResults([]);
            setShowProductSearch(false);
            setSearching(false);
            return;
        }

        // Check cache first
        if (productCache.current.has(term)) {
            setSearchResults(productCache.current.get(term));
            setShowProductSearch(true);
            setSearching(false);
            return;
        }

        try {
            setSearching(true);
            const result = await productActions.searchProducts(term);

            if (result.success) {
                const inStockResults = (result.data || []).filter(
                    p => parseFloat(p.quantity) > 0
                ).slice(0, 20);

                // If customer is selected, fetch last price for each product
                if (selectedCustomer && selectedCustomer.value) {
                    const enhancedResults = await Promise.all(inStockResults.map(async (product) => {
                        const lastPriceData = await fetchLastPriceForCustomer(selectedCustomer.value, product.id);
                        return {
                            ...product,
                            last_price_data: lastPriceData,
                            display_price: lastPriceData ? lastPriceData.last_price : product.selling_price,
                            has_last_price: !!lastPriceData
                        };
                    }));
                    productCache.current.set(term, enhancedResults);
                    setSearchResults(enhancedResults);
                } else {
                    productCache.current.set(term, inStockResults);
                    setSearchResults(inStockResults);
                }
                setShowProductSearch(true);
            }
        } catch (error) {
            console.error('Error searching products:', error);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    // Search products with debounce
    const handleProductSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);

        // Clear previous timeout
        if (productSearchTimeout.current) {
            clearTimeout(productSearchTimeout.current);
        }

        // Set new timeout
        productSearchTimeout.current = setTimeout(() => {
            searchProducts(term);
        }, 300);
    };

    // Calculate all totals with rounding
    const calculateTotals = useCallback((items, splDiscPercent = 0, adjustment = 0, additionalCharge = 0, courierCharge = 0, enableRounding = true) => {
        const no_of_items = items.length;
        const total_qty = items.reduce((sum, i) => sum + (parseFloat(i.quantity || 0)), 0);

        const subtotal = items.reduce((sum, i) => sum + (parseFloat(i.unit_price || 0) * parseFloat(i.quantity || 0)), 0);

        const total_sgst = items.reduce((sum, i) => sum + parseFloat(i.sgst_amount || 0), 0);
        const total_cgst = items.reduce((sum, i) => sum + parseFloat(i.cgst_amount || 0), 0);
        const total_igst = items.reduce((sum, i) => sum + parseFloat(i.igst_amount || 0), 0);

        const discount_amount = items.reduce((sum, i) => sum + parseFloat(i.discount_amount || 0), 0);

        const exd_tax_total = subtotal - discount_amount;

        const spl_disc_amount = (exd_tax_total * parseFloat(splDiscPercent || 0)) / 100;
        const after_spl_disc = exd_tax_total - spl_disc_amount;

        const with_tax = after_spl_disc + total_sgst + total_cgst + total_igst;

        const grand_total = with_tax + parseFloat(adjustment || 0) + parseFloat(additionalCharge || 0) + parseFloat(courierCharge || 0);

        // Calculate rounded values
        let rounded_grand_total = grand_total;
        let round_off = 0;

        if (enableRounding) {
            rounded_grand_total = roundAmount(grand_total);
            round_off = rounded_grand_total - grand_total;
        }

        return {
            no_of_items,
            total_qty,
            subtotal: parseFloat(subtotal.toFixed(2)),
            exd_tax_total: parseFloat(exd_tax_total.toFixed(2)),
            sgst: parseFloat(total_sgst.toFixed(2)),
            cgst: parseFloat(total_cgst.toFixed(2)),
            igst: parseFloat(total_igst.toFixed(2)),
            discount_amount: parseFloat(discount_amount.toFixed(2)),
            spl_disc_amount: parseFloat(spl_disc_amount.toFixed(2)),
            grand_total: parseFloat(grand_total.toFixed(2)),
            rounded_grand_total: parseFloat(rounded_grand_total.toFixed(2)),
            round_off: parseFloat(round_off.toFixed(2))
        };
    }, []);

    // Toggle rounding
    const handleToggleRounding = () => {
        setFormData(prev => {
            const newEnableRounding = !prev.enable_rounding;
            const totals = calculateTotals(
                prev.items,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                newEnableRounding
            );

            const paidAmount = prev.paid_amount;
            const grandTotal = newEnableRounding ? totals.rounded_grand_total : totals.grand_total;

            return {
                ...prev,
                enable_rounding: newEnableRounding,
                ...totals,
                paid_amount: paidAmount > grandTotal ? grandTotal : paidAmount,
                due_amount: grandTotal - (paidAmount > grandTotal ? grandTotal : paidAmount)
            };
        });
    };

    // Add item to sale
    const handleAddItem = async (product) => {
        const existingItem = formData.items.find(i => i.product_id === product.id);

        if (existingItem) {
            const index = formData.items.indexOf(existingItem);
            handleQuantityChange(index, existingItem.quantity + 1);
            setSearchTerm('');
            setShowProductSearch(false);
            return;
        }

        // Determine the unit price
        let unitPrice = parseFloat(product.selling_price || 0);
        let lastPriceInfo = null;

        // If customer is selected, try to get last price
        if (selectedCustomer && selectedCustomer.value) {
            const lastPriceData = await fetchLastPriceForCustomer(selectedCustomer.value, product.id);
            if (lastPriceData && lastPriceData.last_price > 0) {
                unitPrice = lastPriceData.last_price;
                lastPriceInfo = lastPriceData;

                // Show notification that we're using the last price
                Swal.fire({
                    icon: 'info',
                    title: 'Using Last Bill Price',
                    html: `Using last bill price from invoice <strong>${lastPriceData.invoice_no}</strong><br/>
                           Date: ${dayjs(lastPriceData.sale_date).format('DD/MM/YYYY')}<br/>
                           Price: <strong>₹${lastPriceData.last_price.toFixed(2)}</strong>`,
                    timer: 3000,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            }
        }

        const taxPercent = parseFloat(product.tax_rate || 0);
        const sgstPercent = taxPercent / 2;
        const cgstPercent = taxPercent / 2;

        const subtotal = unitPrice * 1;
        const discountAmount = 0;
        const taxableAmount = subtotal - discountAmount;
        const taxAmount = (taxableAmount * taxPercent) / 100;
        const sgstAmount = taxAmount / 2;
        const cgstAmount = taxAmount / 2;
        const totalCost = taxableAmount + taxAmount;

        const newItem = {
            id: `temp_${Date.now()}_${Math.random()}`,
            product_id: product.id,
            product_code: product.sku,
            product_name: product.product_name,
            hsn_code: product.hsn_code || '',
            sku: product.sku,
            units: 'PCS',
            quantity: 1,
            unit_price: unitPrice,
            original_price: product.selling_price, // Store original for reference
            last_price_info: lastPriceInfo,
            discount: 0,
            discount_amount: 0,
            tax_percent: taxPercent,
            sgst_percent: sgstPercent,
            cgst_percent: cgstPercent,
            sgst_amount: sgstAmount,
            cgst_amount: cgstAmount,
            igst_amount: 0,
            unit_cost: parseFloat(product.cost_price || 0),
            total_cost: totalCost,
            category_id: product.category_id,
            brand_id: product.brand_id,
            available_stock: parseFloat(product.quantity || 0)
        };

        setFormData(prev => {
            const updatedItems = [...prev.items, newItem];
            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });

        setSearchTerm('');
        setShowProductSearch(false);
    };

    // Handle quantity change
    const handleQuantityChange = (index, value) => {
        const qty = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const actualQty = Math.max(0, qty);

            const subtotal = item.unit_price * actualQty;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                quantity: actualQty,
                discount_amount: discountAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Handle price change
    const handlePriceChange = (index, value) => {
        const price = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const subtotal = price * item.quantity;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                unit_price: price,
                discount_amount: discountAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Handle discount change
    const handleDiscountChange = (index, value) => {
        const discountPercent = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const subtotal = item.unit_price * item.quantity;
            const discountAmount = (subtotal * discountPercent) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                discount: discountPercent,
                discount_amount: discountAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Handle tax change
    const handleTaxChange = (index, value) => {
        const taxPercent = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const taxableAmount = item.unit_price * item.quantity - (item.discount_amount || 0);
            const taxAmount = (taxableAmount * taxPercent) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                tax_percent: taxPercent,
                sgst_percent: taxPercent / 2,
                cgst_percent: taxPercent / 2,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Remove item
    const handleRemoveItem = (index) => {
        Swal.fire({
            title: 'Remove Item?',
            text: 'Are you sure you want to remove this item?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData(prev => {
                    const updatedItems = prev.items.filter((_, i) => i !== index);
                    const totals = calculateTotals(
                        updatedItems,
                        prev.spl_disc_percent,
                        prev.adjustment,
                        prev.additional_charge,
                        prev.courier_charge,
                        prev.enable_rounding
                    );

                    return {
                        ...prev,
                        items: updatedItems,
                        ...totals
                    };
                });
            }
        });
    };

    // Handle customer change
    const handleCustomerChange = (option) => {
        setSelectedCustomer(option);
        setFormData(prev => ({
            ...prev,
            customer_id: option?.value || '',
            customer_name: option?.label || '',
            customer_phone: option?.phone || ''
        }));

        // Clear product cache when customer changes
        productCache.current.clear();

        // Re-search if there's a search term
        if (searchTerm.length >= 2) {
            searchProducts(searchTerm);
        }
    };

    // Handle date change
    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setFormData(prev => ({ ...prev, sale_date: date.format('YYYY-MM-DD') }));
        }
    };

    // Handle status change
    const handleStatusChange = (option) => {
        setFormData(prev => ({ ...prev, status: option?.value || 'pending' }));
    };

    // Handle special discount percent change
    const handleSplDiscPercentChange = (value) => {
        const percent = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                percent,
                prev.adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );
            return {
                ...prev,
                spl_disc_percent: percent,
                ...totals
            };
        });
    };

    // Handle adjustment change
    const handleAdjustmentChange = (value) => {
        const adjustment = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                prev.spl_disc_percent,
                adjustment,
                prev.additional_charge,
                prev.courier_charge,
                prev.enable_rounding
            );
            return {
                ...prev,
                adjustment,
                ...totals
            };
        });
    };

    // Handle additional charge change
    const handleAdditionalChargeChange = (value) => {
        const charge = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                prev.spl_disc_percent,
                prev.adjustment,
                charge,
                prev.courier_charge,
                prev.enable_rounding
            );
            return {
                ...prev,
                additional_charge: charge,
                ...totals
            };
        });
    };

    // Handle courier charge change
    const handleCourierChargeChange = (value) => {
        const charge = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                prev.spl_disc_percent,
                prev.adjustment,
                prev.additional_charge,
                charge,
                prev.enable_rounding
            );
            return {
                ...prev,
                courier_charge: charge,
                ...totals
            };
        });
    };

    // Handle payment mode change
    const handlePaymentModeChange = (option) => {
        setFormData(prev => ({
            ...prev,
            payment_mode: option?.value || '',
            payment_history: prev.paid_amount > 0 && option?.value ? [
                {
                    date: prev.sale_date,
                    amount: prev.paid_amount,
                    method: option.value,
                    reference: null,
                    notes: 'Initial payment',
                    status: 'completed'
                }
            ] : []
        }));
    };

    // Handle paid amount change
    const handlePaidAmountChange = (value) => {
        const paidAmount = parseFloat(value) || 0;
        const grandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

        setFormData(prev => {
            const validPaidAmount = Math.min(paidAmount, grandTotal);
            const dueAmount = grandTotal - validPaidAmount;

            let paymentStatus = 'unpaid';
            if (validPaidAmount >= grandTotal) {
                paymentStatus = 'paid';
            } else if (validPaidAmount > 0) {
                paymentStatus = 'partial';
            }

            let paymentHistory = [];
            if (validPaidAmount > 0 && prev.payment_mode) {
                paymentHistory = [{
                    date: prev.sale_date,
                    amount: validPaidAmount,
                    method: prev.payment_mode,
                    reference: null,
                    notes: 'Initial payment',
                    status: 'completed'
                }];
            }

            return {
                ...prev,
                paid_amount: validPaidAmount,
                due_amount: dueAmount,
                payment_status: paymentStatus,
                payment_history: paymentHistory
            };
        });
    };

    // Handle customer added from modal
    const handleCustomerAdded = (newCustomer) => {
        console.log('Customer added callback received:', newCustomer);

        let customerData = newCustomer;
        if (newCustomer.data) {
            customerData = newCustomer.data;
        }
        if (newCustomer.customer) {
            customerData = newCustomer.customer;
        }

        if (!customerData || !customerData.id) {
            console.error('Invalid customer data received:', newCustomer);
            Swal.fire('Error', 'Invalid customer data received', 'error');
            return;
        }

        loadCustomers();

        const customerOption = {
            value: customerData.id,
            label: `${customerData.name} ${customerData.phone ? `(${customerData.phone})` : ''}`,
            phone: customerData.phone,
            email: customerData.email,
            address: customerData.address
        };

        setSelectedCustomer(customerOption);
        setFormData(prev => ({
            ...prev,
            customer_id: customerData.id,
            customer_name: customerOption.label,
            customer_phone: customerData.phone || ''
        }));

        Swal.fire({
            icon: 'success',
            title: 'Customer Added',
            text: `${customerData.name} has been added successfully`,
            timer: 1500,
            showConfirmButton: false
        });
    };

    // Validate form
    const validateForm = () => {
        if (!formData.customer_id) {
            Swal.fire('Error', 'Please select a customer', 'error');
            return false;
        }

        if (formData.items.length === 0) {
            Swal.fire('Error', 'Please add at least one item', 'error');
            return false;
        }

        // Stock availability check
        for (const item of formData.items) {
            const available = parseFloat(item.available_stock ?? Infinity);
            const requested = parseFloat(item.quantity || 0);
            if (requested > available) {
                Swal.fire({
                    icon: 'error',
                    title: 'Insufficient Stock',
                    html: `<b>${item.product_name}</b><br/>
                           You entered <b>${requested}</b> qty, but only <b>${available}</b> units are available in stock.`
                });
                return false;
            }
        }

        return true;
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setLoading(true);

            const itemsToSend = formData.items.map(({ ...item }) => item);
            const finalGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

            const dataToSend = {
                ...formData,
                items: itemsToSend,
                grand_total: finalGrandTotal,
                round_off: formData.round_off,
                payment_history: formData.payment_history,
                paid_amount: Math.min(formData.paid_amount, finalGrandTotal),
                due_amount: finalGrandTotal - Math.min(formData.paid_amount, finalGrandTotal)
            };

            console.log('Sending data:', JSON.stringify(dataToSend, null, 2));

            const result = await saleActions.createSale(dataToSend);

            if (result.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Sale created successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate('/sales-list');
            } else {
                console.error('Error response:', result);
                Swal.fire('Error', result.message || 'Failed to create sale', 'error');
            }
        } catch (error) {
            console.error('Error creating sale:', error);
            Swal.fire('Error', error.message || 'Failed to create sale', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        if (formData.items.length > 0 || formData.customer_id) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/sales-list');
                }
            });
        } else {
            navigate('/sales-list');
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toFixed(2)}`;
    };

    // ── Numeric input helpers (raw-string pattern) ──
    // Returns the raw typed string while editing, else the numeric value as string
    const getRaw = (key, numericVal) =>
        key in rawInputValues ? rawInputValues[key] : String(numericVal ?? 0);

    // On focus: if value is 0, clear so user can start typing immediately
    const onNumFocus = (key, numericVal) => {
        if (parseFloat(numericVal) === 0) {
            setRawInputValues(prev => ({ ...prev, [key]: '' }));
        }
    };

    // On blur: if still empty, remove the raw key (reverts display to numeric value)
    const onNumBlur = (key) => {
        const raw = rawInputValues[key];
        if (raw === '' || raw === undefined) {
            setRawInputValues(prev => { const c = { ...prev }; delete c[key]; return c; });
        }
    };

    // On change: store raw string + call the numeric change handler
    const onNumChange = (key, value, numericSetter) => {
        setRawInputValues(prev => ({ ...prev, [key]: value }));
        numericSetter(value);
    };

    // Cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        return () => {
            if (productSearchTimeout.current) {
                clearTimeout(productSearchTimeout.current);
            }
        };
    }, []);

    // Clear raw display values when item rows change (add/remove)
    useEffect(() => {
        setRawInputValues({});
    }, [formData.items.length]);

    // Get the current grand total based on rounding setting
    const currentGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Add Sale</h4>
                        <h6>Create new sales invoice</h6>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleCancel}
                            >
                                <ArrowLeft size={16} className="me-2" />
                                Back to Sales
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Form */}
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            {/* Bill Number and Reference Row */}
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Bill Number</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={formData.bill_number}
                                        readOnly
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Sales Reference</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={formData.sales_reference}
                                        readOnly
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Date</label>
                                    <DatePicker
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        format="DD/MM/YYYY"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                        allowClear={false}
                                    />
                                </div>
                            </div>

                            {/* Customer Row */}
                            <div className="row mb-3">
                                <div className="col-md-5">
                                    <label className="form-label fw-bold">Customer Name</label>
                                    <div className="d-flex">
                                        <div style={{ flex: 1 }}>
                                            {loadingCustomers ? (
                                                <div className="d-flex align-items-center">
                                                    <div className="spinner-border spinner-border-sm text-primary me-2" />
                                                    <span>Loading customers...</span>
                                                </div>
                                            ) : (
                                                <Select
                                                    options={customers}
                                                    value={selectedCustomer}
                                                    onChange={handleCustomerChange}
                                                    onInputChange={handleCustomerSearch}
                                                    placeholder={customers.length === 0 ? "No customers found" : "Choose customer"}
                                                    isClearable
                                                    noOptionsMessage={() => customerError || "No customers found"}
                                                    isLoading={loadingCustomers}
                                                />
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary ms-2"
                                            onClick={() => setShowCustomerModal(true)}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                    {customerError && (
                                        <small className="text-danger">{customerError}</small>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Phone</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.customer_phone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                                        placeholder="Enter phone number"
                                    />
                                </div>

                            </div>

                            {/* Product Search */}
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">Product Name / Code</label>
                                    <div className="position-relative" ref={searchDropdownRef}>
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            className="form-control"
                                            placeholder="Type product name or code..."
                                            value={searchTerm}
                                            onChange={handleProductSearch}
                                        />

                                        {searching && (
                                            <div className="position-absolute end-0 top-0 mt-2 me-3">
                                                <div className="spinner-border spinner-border-sm text-primary" />
                                            </div>
                                        )}

                                        {showProductSearch && searchResults.length > 0 && (
                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 9999, maxHeight: '300px', overflowY: 'auto' }}>
                                                {searchResults.map(product => (
                                                    <div
                                                        key={product.id}
                                                        className="p-2 border-bottom product-search-item"
                                                        onClick={() => handleAddItem(product)}
                                                        style={{ cursor: 'pointer' }}
                                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f4ff'}
                                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <strong>{product.product_name}</strong>
                                                                <br />
                                                                <small className="text-muted">Code: {product.sku}</small>
                                                                {product.has_last_price && (
                                                                    <small className="text-muted d-block">
                                                                        Last bill: ₹{product.last_price_data?.last_price?.toFixed(2)}
                                                                        ({dayjs(product.last_price_data?.sale_date).format('DD/MM/YYYY')})
                                                                    </small>
                                                                )}
                                                            </div>
                                                            <div className="text-end">
                                                                {product.has_last_price ? (
                                                                    <>
                                                                        <span className="text-primary fw-bold">₹{product.last_price_data?.last_price?.toFixed(2)}</span>
                                                                        <br />
                                                                        <small className="text-muted text-decoration-line-through">
                                                                            ₹{product.selling_price?.toFixed(2)}
                                                                        </small>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-primary fw-bold">₹{product.selling_price?.toFixed(2)}</span>
                                                                )}
                                                                <br />
                                                                {parseFloat(product.quantity) > 0 && (
                                                                    <small className="badge bg-success-subtle text-success">Stock: {product.quantity}</small>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* DC Number Row */}
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">PO Number</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.po_number}
                                        onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                                        placeholder="Enter PO number"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">DC Number</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.dc_number}
                                        onChange={(e) => setFormData(prev => ({ ...prev, dc_number: e.target.value }))}
                                        placeholder="Enter DC number"
                                    />
                                </div>
                            </div>

                            {/* Items Table */}
                            {formData.items.length > 0 && (
                                <div className="table-responsive mb-4">
                                    <table className="table table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Product Code</th>
                                                <th>Product Name</th>
                                                <th>HSN Code</th>
                                                <th>Units</th>
                                                <th className="text-end">Qty</th>
                                                <th className="text-end">Sales Rate (Excl.Tax)</th>
                                                <th className="text-end">Disc %</th>
                                                <th className="text-end">SGST %</th>
                                                <th className="text-end">CGST %</th>
                                                <th className="text-end">IGST %</th>
                                                <th className="text-end">Amount (Incl.Tax)</th>
                                                <th className="text-center">Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.items.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            value={item.product_code || ''}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td>
                                                        <strong>{item.product_name}</strong>
                                                        {item.last_price_info && (
                                                            <small className="text-muted d-block">
                                                                Last bill: ₹{item.last_price_info.last_price.toFixed(2)}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm bg-light"
                                                            value={item.hsn_code || ''}
                                                            readOnly
                                                            style={{ width: '80px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={item.units}
                                                            onChange={(e) => {
                                                                const updated = [...formData.items];
                                                                updated[index].units = e.target.value;
                                                                setFormData(prev => ({ ...prev, items: updated }));
                                                            }}
                                                        >
                                                            <option value="PCS">PCS</option>
                                                            <option value="BOX">BOX</option>
                                                            <option value="KG">KG</option>
                                                            <option value="LTR">LTR</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value={getRaw(`qty_${index}`, item.quantity)}
                                                            onChange={(e) => onNumChange(`qty_${index}`, e.target.value, (v) => handleQuantityChange(index, v))}
                                                            onFocus={() => onNumFocus(`qty_${index}`, item.quantity)}
                                                            onBlur={() => onNumBlur(`qty_${index}`)}
                                                            min="0"
                                                            step="any"
                                                            style={{ width: '80px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value={getRaw(`price_${index}`, item.unit_price)}
                                                            onChange={(e) => onNumChange(`price_${index}`, e.target.value, (v) => handlePriceChange(index, v))}
                                                            onFocus={() => onNumFocus(`price_${index}`, item.unit_price)}
                                                            onBlur={() => onNumBlur(`price_${index}`)}
                                                            min="0"
                                                            step="any"
                                                            style={{ width: '100px' }}
                                                        />
                                                        {item.last_price_info && item.unit_price !== item.original_price && (
                                                            <small className="text-muted">
                                                                Original: ₹{item.original_price?.toFixed(2)}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value={getRaw(`disc_${index}`, item.discount || 0)}
                                                            onChange={(e) => onNumChange(`disc_${index}`, e.target.value, (v) => handleDiscountChange(index, v))}
                                                            onFocus={() => onNumFocus(`disc_${index}`, item.discount || 0)}
                                                            onBlur={() => onNumBlur(`disc_${index}`)}
                                                            min="0"
                                                            step="any"
                                                            style={{ width: '70px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value={getRaw(`sgst_${index}`, item.sgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                onNumChange(`sgst_${index}`, val, (v) => handleTaxChange(index, (parseFloat(v) || 0) * 2));
                                                            }}
                                                            onFocus={() => onNumFocus(`sgst_${index}`, item.sgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                            onBlur={() => onNumBlur(`sgst_${index}`)}
                                                            min="0"
                                                            step="any"
                                                            style={{ width: '70px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value={getRaw(`cgst_${index}`, item.cgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                onNumChange(`cgst_${index}`, val, (v) => handleTaxChange(index, (parseFloat(v) || 0) * 2));
                                                            }}
                                                            onFocus={() => onNumFocus(`cgst_${index}`, item.cgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                            onBlur={() => onNumBlur(`cgst_${index}`)}
                                                            min="0"
                                                            step="any"
                                                            style={{ width: '70px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value="0"
                                                            readOnly
                                                            style={{ width: '70px', backgroundColor: '#f8f9fa' }}
                                                        />
                                                    </td>
                                                    <td className="text-end fw-bold">
                                                        {formatCurrency(item.total_cost)}
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm"
                                                            onClick={() => handleRemoveItem(index)}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Summary Section */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="row">
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">No of Items</span>
                                                <span className="fw-bold text-primary">{formData.no_of_items}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Exd Tax Total</span>
                                                <span className="fw-bold text-success">{formatCurrency(formData.exd_tax_total)}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Total Qty</span>
                                                <span className="fw-bold text-info">{formData.total_qty}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">SGST</span>
                                                <span className="fw-bold text-warning">{formatCurrency(formData.sgst)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="row">
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Sub Total</span>
                                                <span className="fw-bold text-primary">{formatCurrency(formData.subtotal)}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Spl Disc %</span>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm w-50 text-end"
                                                    value={getRaw('spl_disc', formData.spl_disc_percent)}
                                                    onChange={(e) => onNumChange('spl_disc', e.target.value, handleSplDiscPercentChange)}
                                                    onFocus={() => onNumFocus('spl_disc', formData.spl_disc_percent)}
                                                    onBlur={() => onNumBlur('spl_disc')}
                                                    step="any"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Discount Amount</span>
                                                <span className="fw-bold text-danger">{formatCurrency(formData.discount_amount)}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">CGST</span>
                                                <span className="fw-bold text-danger">{formatCurrency(formData.cgst)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Charges and Grand Total Row with Rounding */}
                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                        <span className="fw-semibold">Additional Charge</span>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm w-50 text-end"
                                            value={getRaw('add_charge', formData.additional_charge)}
                                            onChange={(e) => onNumChange('add_charge', e.target.value, handleAdditionalChargeChange)}
                                            onFocus={() => onNumFocus('add_charge', formData.additional_charge)}
                                            onBlur={() => onNumBlur('add_charge')}
                                            min="0"
                                            step="any"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                        <span className="fw-semibold">Courier Charge</span>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm w-50 text-end"
                                            value={getRaw('courier', formData.courier_charge)}
                                            onChange={(e) => onNumChange('courier', e.target.value, handleCourierChargeChange)}
                                            onFocus={() => onNumFocus('courier', formData.courier_charge)}
                                            onBlur={() => onNumBlur('courier')}
                                            min="0"
                                            step="any"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                        <span className="fw-semibold">Adjustment (+/-)</span>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm w-50 text-end"
                                            value={getRaw('adjustment', formData.adjustment ?? 0)}
                                            onChange={(e) => onNumChange('adjustment', e.target.value, handleAdjustmentChange)}
                                            onFocus={() => onNumFocus('adjustment', formData.adjustment ?? 0)}
                                            onBlur={() => onNumBlur('adjustment')}
                                            step="any"
                                            title="Use positive value to add, negative to deduct"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="bg-primary text-white p-3 rounded">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h5 className="mb-0 text-white">Grand Total</h5>
                                            <div className="d-flex align-items-center">
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${formData.enable_rounding ? 'btn-light' : 'btn-outline-light'} me-2`}
                                                    onClick={handleToggleRounding}
                                                    title={formData.enable_rounding ? "Rounding Enabled" : "Rounding Disabled"}
                                                >
                                                    <RotateCcw size={14} className="me-1" />
                                                    {formData.enable_rounding ? 'Rounded' : 'Actual'}
                                                </button>
                                                <h3 className="mb-0 text-white">
                                                    {formatCurrency(currentGrandTotal)}
                                                </h3>
                                            </div>
                                        </div>
                                        {formData.enable_rounding && formData.round_off !== 0 && (
                                            <div className="d-flex justify-content-end">
                                                <small className="text-white-50">
                                                    (Actual: {formatCurrency(formData.grand_total)} |
                                                    Round Off: {formData.round_off > 0 ? '+' : ''}{formatCurrency(formData.round_off)})
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Payment Row */}
                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Paid Amount</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={getRaw('paid', formData.paid_amount)}
                                        onChange={(e) => onNumChange('paid', e.target.value, handlePaidAmountChange)}
                                        onFocus={() => onNumFocus('paid', formData.paid_amount)}
                                        onBlur={() => onNumBlur('paid')}
                                        min="0"
                                        max={currentGrandTotal}
                                        step="any"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Due Amount</label>
                                    <input
                                        type="text"
                                        className={`form-control ${formData.due_amount > 0 ? 'text-danger' : 'text-success'} bg-light`}
                                        value={formatCurrency(formData.due_amount)}
                                        readOnly
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Mode of Payment</label>
                                    <Select
                                        options={paymentModeOptions}
                                        value={paymentModeOptions.find(opt => opt.value === formData.payment_mode)}
                                        onChange={handlePaymentModeChange}
                                        placeholder="Mode of Payment"
                                        isClearable
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Payment Status</label>
                                    <Select
                                        options={paymentStatusOptions}
                                        value={paymentStatusOptions.find(opt => opt.value === formData.payment_status)}
                                        onChange={(opt) => setFormData(prev => ({ ...prev, payment_status: opt?.value || 'unpaid' }))}
                                        placeholder="Payment Status"
                                        isClearable
                                    />
                                </div>
                            </div>

                            {/* Status Row */}
                            <div className="row mb-3">
                                <div className="col-md-9"></div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Status</label>
                                    <Select
                                        options={statusOptions}
                                        value={statusOptions.find(opt => opt.value === formData.status)}
                                        onChange={handleStatusChange}
                                        placeholder="Select Status"
                                    />
                                </div>
                            </div>

                            {/* Remarks Row */}
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">Remarks</label>
                                    <textarea
                                        className="form-control"
                                        rows="2"
                                        value={formData.remarks}
                                        onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                        placeholder="Enter remarks"
                                    />
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={handleCancel}
                                >
                                    <X size={16} className="me-2" />
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading || formData.items.length === 0}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-2" />
                                            Save Product
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Add Customer Modal */}
            {showCustomerModal && (
                <AddCustomer
                    isOpen={showCustomerModal}
                    onClose={() => setShowCustomerModal(false)}
                    onSuccess={handleCustomerAdded}
                />
            )}
        </div>
    );
};

export default AddSalePage;